import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Checkbox,
  IconButton,
  Chip,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  Visibility as ReviewIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Timestamp } from 'firebase/firestore';
import { TeamMember, User, PersonalRecord } from '../../types';
import { getFirstName } from '../../utils/sortByFirstName';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { avatarService } from '../../services/avatarService';
import { activityService } from '../../services/activityService';
import { relayService } from '../../services/relayService';
import { format } from 'date-fns';
import { minutesFromUserParts, formatMinutesAsRaceTime } from '../../utils/prTimeFormat';

function clockPartsToMinutes(h: string, m: string, s: string): number {
  return minutesFromUserParts(Number(h) || 0, Number(m) || 0, Number(s) || 0);
}

interface BulkRaceResultsEntryProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
}

interface AthleteResult {
  member: TeamMember;
  user: User;
  selected: boolean;
  hours: string;
  minutes: string;
  seconds: string;
  dnf: boolean;
  existingPR: PersonalRecord | null;
  isPRBeat: boolean;
}

const EVENT_OPTIONS = [
  { value: '400m', label: '400m' },
  { value: '800m', label: '800m' },
  { value: '1600m', label: '1600m (Mile)' },
  { value: '3200m', label: '3200m (2 Mile)' },
  { value: '3k', label: '3k' },
  { value: '5K', label: '5K' },
  { value: '10K', label: '10K' },
  { value: '4x100', label: '4x100' },
  { value: '4x400', label: '4x400' },
  { value: '4x800', label: '4x800' },
  { value: 'DMR', label: 'DMR' },
  { value: 'Half Marathon', label: 'Half Marathon' },
  { value: 'Marathon', label: 'Marathon' },
];

const RELAY_EVENT_TYPES = new Set(['4x800', '4x100', '4x400', 'DMR']);
/** Legs sum must match overall within 0.01 s (hundredth-second); small epsilon for float noise */
const RELAY_SUM_TOLERANCE_SEC = 0.01;

// Helper function to convert event type to distance in miles
const eventTypeToMiles = (eventType: string): number => {
  const conversions: Record<string, number> = {
    '400m': 400 / 1609.34, // meters to miles
    '800m': 800 / 1609.34,
    '1600m': 1600 / 1609.34, // ~0.994 miles (1 mile)
    '3200m': 3200 / 1609.34, // ~1.988 miles (2 miles)
    '3k': 3000 / 1609.34, // ~1.864 miles
    '5K': 5000 / 1609.34, // ~3.107 miles
    '10K': 10000 / 1609.34, // ~6.214 miles
    'Half Marathon': 13.1,
    'Marathon': 26.2,
  };
  return conversions[eventType] || 0;
};

const BulkRaceResultsEntry: React.FC<BulkRaceResultsEntryProps> = ({
  open,
  onClose,
  teamId,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [eventType, setEventType] = useState<string>('5K');
  const [raceDate, setRaceDate] = useState<Date | null>(new Date());
  const [location, setLocation] = useState('');
  const [athletes, setAthletes] = useState<AthleteResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  // Relay-specific entry state (coach enters overall time + 4 leg split times)
  const [relayLegAthleteIds, setRelayLegAthleteIds] = useState<string[]>(['', '', '', '']);
  const [relayOverallHours, setRelayOverallHours] = useState<string>('');
  const [relayOverallMinutes, setRelayOverallMinutes] = useState<string>('');
  const [relayOverallSeconds, setRelayOverallSeconds] = useState<string>('');
  const [relayLegHours, setRelayLegHours] = useState<string[]>(['', '', '', '']);
  const [relayLegMinutes, setRelayLegMinutes] = useState<string[]>(['', '', '', '']);
  const [relayLegSeconds, setRelayLegSeconds] = useState<string[]>(['', '', '', '']);
  const [results, setResults] = useState<{
    created: number;
    updated: number;
    skipped: number;
    details: Array<{ userName: string; action: string; message: string }>;
  } | null>(null);

  useEffect(() => {
    if (open) {
      loadAthletes();
      setEventType('5K');
      setRaceDate(new Date());
      setLocation('');
      setShowResults(false);
      setReviewMode(false);
      setRelayLegAthleteIds(['', '', '', '']);
      setRelayOverallHours('');
      setRelayOverallMinutes('');
      setRelayOverallSeconds('');
      setRelayLegHours(['', '', '', '']);
      setRelayLegMinutes(['', '', '', '']);
      setRelayLegSeconds(['', '', '', '']);
      setResults(null);
      setError('');
      setSuccess('');
    }
  }, [open, teamId]);

  const loadAthletes = async () => {
    setLoading(true);
    setError('');
    
    try {
      const members = await teamService.getTeamMembers(teamId);
      console.log('Loaded team members:', members.map(m => ({ 
        id: m.id, 
        athleteId: m.athleteId, 
        tags: m.tags 
      })));
      
      const athletesData: AthleteResult[] = [];
      for (const member of members) {
        const user = await userService.getUserById(member.athleteId);
        if (user) {
          // Auto-select if athlete has tag for current event
          const hasTag = member.tags?.includes(eventType) || false;
          
          athletesData.push({
            member,
            user,
            selected: hasTag, // Auto-select based on tag
            hours: '',
            minutes: '',
            seconds: '',
            dnf: false,
            existingPR: null,
            isPRBeat: false,
          });
        }
      }
      
      console.log('Loaded athletes with tags:', athletesData.map(a => ({
        name: a.user.displayName,
        tags: a.member.tags,
        selected: a.selected
      })));
      athletesData.sort((a, b) =>
        getFirstName(a.user.displayName).localeCompare(getFirstName(b.user.displayName), undefined, { sensitivity: 'base' })
      );
      setAthletes(athletesData);
    } catch (err: any) {
      setError('Failed to load athletes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-select athletes when event changes
  useEffect(() => {
    if (athletes.length > 0 && eventType) {
      console.log('Event changed to:', eventType, '- Re-evaluating athlete selection');
      handleEventChange();
    }
  }, [eventType, athletes.length]);

  // Prefill relay lineup from tags when a relay event is chosen.
  useEffect(() => {
    if (!RELAY_EVENT_TYPES.has(eventType) || athletes.length === 0) return;

    const eligible = athletes
      .filter((a) => a.user.role !== 'coach' && a.user.role !== 'headCoach')
      .map((a) => ({
        id: a.user.id,
        tags: a.member.tags || [],
      }));

    if (eligible.length === 0) return;

    const used = new Set<string>();

    const pick = (preferredTags: string[], fallbackIndexStart: number): string => {
      const direct = eligible.find((a) => !used.has(a.id) && preferredTags.some((t) => a.tags.includes(t)));
      if (direct) {
        used.add(direct.id);
        return direct.id;
      }
      // Fallback: first unused (stable via athletes sort order)
      const fallback = eligible.find((a) => !used.has(a.id));
      if (fallback) {
        used.add(fallback.id);
        return fallback.id;
      }
      return '';
    };

    const legPreferredTags: string[][] =
      eventType === '4x800'
        ? [['4x800', '800m'], ['4x800', '800m'], ['4x800', '800m'], ['4x800', '800m']]
        : eventType === '4x400'
          ? [['4x400', '400m'], ['4x400', '400m'], ['4x400', '400m'], ['4x400', '400m']]
          : eventType === '4x100'
            ? [['4x100', '100m'], ['4x100', '100m'], ['4x100', '100m'], ['4x100', '100m']]
            : [
                ['DMR', '1200m', '1600m', '3k'],
                ['DMR', '400m'],
                ['DMR', '800m'],
                ['DMR', '1600m', '3k'],
              ];

    const nextLegs: string[] = [0, 1, 2, 3].map((i) => pick(legPreferredTags[i], 0));
    setRelayLegAthleteIds(nextLegs);

    // Clear times when switching relay events so coach doesn't accidentally save wrong splits.
    setRelayOverallHours('');
    setRelayOverallMinutes('');
    setRelayOverallSeconds('');
    setRelayLegHours(['', '', '', '']);
    setRelayLegMinutes(['', '', '', '']);
    setRelayLegSeconds(['', '', '', '']);
  }, [eventType, athletes.length]);

  const handleEventChange = async () => {
    console.log('Event changed to:', eventType, '- Processing selection and PRs');
    
    // First, update selection based on tags
    const updatedAthletes = athletes.map(athlete => {
      const hasTag = athlete.member.tags?.includes(eventType) || false;
      console.log(`  ${athlete.user.displayName}: tags=${JSON.stringify(athlete.member.tags)}, hasTag=${hasTag}, willSelect=${hasTag}`);
      return {
        ...athlete,
        selected: hasTag, // This should reset selection based on current eventType
        isPRBeat: false, // Reset PR beat indicator when event changes
      };
    });
    
    console.log('Updated selections:', updatedAthletes.map(a => ({ name: a.user.displayName, selected: a.selected })));
    
    // Then, load existing PRs for the updated athletes
    for (let i = 0; i < updatedAthletes.length; i++) {
      const athlete = updatedAthletes[i];
      const existingPR = await avatarService.getAthleteExistingPRForEvent(
        athlete.user.id,
        eventType
      );
      updatedAthletes[i].existingPR = existingPR;
    }
    
    // Finally, update the state with both selection and PR data
    setAthletes(updatedAthletes);
  };

  const handleToggleSelect = (index: number) => {
    setAthletes(prev => {
      const updated = [...prev];
      updated[index].selected = !updated[index].selected;
      return updated;
    });
  };

  const handleSelectAll = () => {
    setAthletes(prev => prev.map(a => ({ ...a, selected: true })));
  };

  const handleClearAll = () => {
    setAthletes(prev => prev.map(a => ({ ...a, selected: false })));
  };

  const handleTimeChange = (index: number, field: 'hours' | 'minutes' | 'seconds', value: string) => {
    setAthletes(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      
      // Check if this time beats existing PR (seconds may include hundredths, e.g. 45.67)
      const athlete = updated[index];
      const hasTime =
        athlete.hours !== '' ||
        athlete.minutes !== '' ||
        athlete.seconds !== '';
      if (athlete.existingPR && hasTime && !athlete.dnf) {
        const newTimeMinutes = clockPartsToMinutes(athlete.hours, athlete.minutes, athlete.seconds);
        
        updated[index].isPRBeat = avatarService.isTimeFaster(newTimeMinutes, athlete.existingPR.time);
      } else {
        updated[index].isPRBeat = false;
      }
      
      return updated;
    });
  };

  const handleDNFChange = (index: number, checked: boolean) => {
    setAthletes(prev => {
      const updated = [...prev];
      updated[index].dnf = checked;
      if (checked) {
        // Clear times if marked DNF
        updated[index].hours = '';
        updated[index].minutes = '';
        updated[index].seconds = '';
        updated[index].isPRBeat = false;
      }
      return updated;
    });
  };

  const athletesWithTimeToSave = athletes.filter(
    a => a.selected && !a.dnf && (a.hours || a.minutes || a.seconds)
  );

  const isRelayEvent = RELAY_EVENT_TYPES.has(eventType);

  const relayOverallFilled =
    relayOverallHours !== '' || relayOverallMinutes !== '' || relayOverallSeconds !== '';

  const relayLegsFilled =
    relayLegHours.every((_, i) => {
      const h = relayLegHours[i];
      const m = relayLegMinutes[i];
      const s = relayLegSeconds[i];
      return h !== '' || m !== '' || s !== '';
    }) &&
    relayLegAthleteIds.every((id) => id !== '');

  const relayLegsUnique = new Set(relayLegAthleteIds).size === 4;
  const relayOverallTotalMinutesComputed = clockPartsToMinutes(
    relayOverallHours,
    relayOverallMinutes,
    relayOverallSeconds
  );

  const relayLegTotalMinutes = [
    clockPartsToMinutes(relayLegHours[0], relayLegMinutes[0], relayLegSeconds[0]),
    clockPartsToMinutes(relayLegHours[1], relayLegMinutes[1], relayLegSeconds[1]),
    clockPartsToMinutes(relayLegHours[2], relayLegMinutes[2], relayLegSeconds[2]),
    clockPartsToMinutes(relayLegHours[3], relayLegMinutes[3], relayLegSeconds[3]),
  ];

  const relaySumDeltaMinutes = Math.abs(
    relayLegTotalMinutes.reduce((a, b) => a + b, 0) - relayOverallTotalMinutesComputed
  );
  /** Match legs sum to overall within 0.01 s (hundredth of a second); tiny epsilon for float noise */
  const relaySumDeltaSec = relaySumDeltaMinutes * 60;
  const showRelaySumWarning =
    isRelayEvent &&
    relayLegsFilled &&
    relayOverallFilled &&
    relaySumDeltaSec > RELAY_SUM_TOLERANCE_SEC + 1e-6;

  const goToReview = () => {
    setError('');
    if (!raceDate) {
      setError('Please select a race date');
      return;
    }
    if (isRelayEvent) {
      if (!relayLegsUnique) {
        setError('Select 4 different athletes for the relay (legs cannot repeat the same athlete).');
        return;
      }
      if (!relayLegsFilled) {
        setError('Enter all 4 leg athletes and each leg split time.');
        return;
      }
      if (!relayOverallFilled) {
        setError('Enter the overall relay time.');
        return;
      }
      setReviewMode(true);
      return;
    }

    if (selectedAthletes.length === 0) {
      setError('Select at least one athlete');
      return;
    }
    if (athletesWithTimeToSave.length === 0) {
      setError('Enter a finish time for at least one athlete (or mark DNF only if you are not saving times). To save PRs you need at least one time.');
      return;
    }
    setReviewMode(true);
  };

  const handleSaveResults = async () => {
    if (!raceDate) {
      setError('Please select a race date');
      return;
    }

    if (isRelayEvent) {
      try {
        setSaving(true);
        setError('');

        const relayDateString = format(raceDate, 'yyyy-MM-dd');

        const overallMinutes = clockPartsToMinutes(
          relayOverallHours,
          relayOverallMinutes,
          relayOverallSeconds
        );

        const legMinutes: [number, number, number, number] = [
          clockPartsToMinutes(relayLegHours[0], relayLegMinutes[0], relayLegSeconds[0]),
          clockPartsToMinutes(relayLegHours[1], relayLegMinutes[1], relayLegSeconds[1]),
          clockPartsToMinutes(relayLegHours[2], relayLegMinutes[2], relayLegSeconds[2]),
          clockPartsToMinutes(relayLegHours[3], relayLegMinutes[3], relayLegSeconds[3]),
        ];

        const legIds = relayLegAthleteIds as [string, string, string, string];

        const overallOk =
          relayLegsUnique &&
          relayLegsFilled &&
          relayOverallFilled;

        if (!overallOk) {
          setError('Relay entry is incomplete.');
          return;
        }

        const sumLegsMinutes = legMinutes.reduce((a, b) => a + b, 0);
        const deltaSec = Math.abs(sumLegsMinutes - overallMinutes) * 60;
        const sumMatches = deltaSec <= RELAY_SUM_TOLERANCE_SEC + 1e-6;

        const result = await relayService.upsertRelayResult({
          teamId,
          eventType: eventType as any,
          date: relayDateString,
          location: location.trim() || undefined,
          overallTimeMinutes: overallMinutes,
          legTimesMinutes: legMinutes as any,
          legAthleteIds: legIds as any,
          isVerified: true,
        });

        setResults({
          created: result.created ? 1 : 0,
          updated: result.created ? 0 : 1,
          skipped: 0,
          details: [
            {
              userName: `${legIds.join(', ')}`,
              action: result.created ? 'created' : 'updated',
              message: `Relay overall + legs saved.${sumMatches ? '' : ` Warning: legs sum differs by ${deltaSec.toFixed(2)}s.`}`,
            },
          ],
        });

        setShowResults(true);
        setReviewMode(false);
        setSuccess(`Relay results saved!`);
      } catch (err: any) {
        setError(err.message || 'Failed to save relay results');
        console.error(err);
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      // Prepare PR entries for athletes with times
      const prEntries = athletes
        .filter(a => a.selected && !a.dnf && (a.hours || a.minutes || a.seconds))
        .map(athlete => {
          const totalMinutes = clockPartsToMinutes(athlete.hours, athlete.minutes, athlete.seconds);
          
          return {
            userId: athlete.user.id,
            teamId,
            prData: {
              eventType: eventType as any,
              time: totalMinutes,
              date: Timestamp.fromDate(raceDate),
              location: location.trim() || undefined,
              isVerified: true, // Coach verified
            }
          };
        });

      if (prEntries.length === 0) {
        setError('Please enter times for at least one athlete');
        setSaving(false);
        return;
      }

      // Bulk upsert PRs
      const bulkResults = await avatarService.bulkUpsertPRs(prEntries);
      
      // Also create/update DailyActivity entries for Race Day runs
      const raceDateString = format(raceDate, 'yyyy-MM-dd');
      const distanceInMiles = eventTypeToMiles(eventType);
      
      // Create/update activities for each athlete with a race time
      const activityWarnings: string[] = [];
      const activityPromises = athletes
        .filter(a => a.selected && !a.dnf && (a.hours || a.minutes || a.seconds))
        .map(async (athlete) => {
          try {
            const totalMinutes = clockPartsToMinutes(athlete.hours, athlete.minutes, athlete.seconds);

            const existingActivities = await activityService.getUserActivitiesForDate(
              athlete.user.id,
              raceDateString,
              teamId
            );

            const activityData: Record<string, unknown> = {
              sleepScore: 0,
              hydrationLevel: 0,
              nutritionLevel: 0,
              runType: 'Run' as const,
              runSubType: 'Race Day' as const,
              milesRun: distanceInMiles,
              crossTrainingMinutes: 0,
              strengthMinutes: 0,
              raceEventType: eventType,
              raceTime: totalMinutes,
            };

            if (existingActivities.length > 0) {
              await activityService.patchDailyActivityFields(existingActivities[0].id, activityData);
            } else {
              const result = await activityService.submitDailyActivityForDate(
                athlete.user.id,
                teamId,
                raceDateString,
                activityData as any
              );

              if (result.success) {
                const newActivities = await activityService.getUserActivitiesForDate(
                  athlete.user.id,
                  raceDateString,
                  teamId
                );
                if (newActivities.length > 0) {
                  await activityService.patchDailyActivityFields(newActivities[0].id, {
                    raceEventType: eventType,
                    raceTime: totalMinutes,
                  });
                }
              }
            }
          } catch (actErr: any) {
            const name = athlete.user.displayName || 'Athlete';
            activityWarnings.push(`${name}: ${actErr?.message || 'daily activity sync failed'}`);
            console.error('[BulkRaceResults] activity sync failed for', name, actErr);
          }
        });
      
      await Promise.all(activityPromises);
      
      // Map results to user names
      const detailedResults = bulkResults.results.map(result => {
        const athlete = athletes.find(a => a.user.id === result.userId);
        return {
          userName: athlete?.user.displayName || 'Unknown',
          action: result.action,
          message: result.message,
        };
      });

      setResults({
        created: bulkResults.created,
        updated: bulkResults.updated,
        skipped: bulkResults.skipped,
        details: detailedResults,
      });
      
      setShowResults(true);
      const actMsg =
        activityWarnings.length > 0
          ? ` Daily activity note: ${activityWarnings.join(' ')}`
          : ' Daily activities updated.';
      setSuccess(
        `Race results saved! ${bulkResults.created} new PRs, ${bulkResults.updated} PRs updated.${actMsg}`
      );
    } catch (err: any) {
      setError(err.message || 'Failed to save race results');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (showResults) {
      setShowResults(false);
      setResults(null);
    }
    setReviewMode(false);
    onClose();
  };

  const selectedAthletes = athletes.filter(a => a.selected);

  const formatEnteredRaceTime = (a: AthleteResult): string => {
    if (a.dnf) return 'DNF';
    const has = a.hours || a.minutes || a.seconds;
    if (!has) return '—';
    return formatMinutesAsRaceTime(clockPartsToMinutes(a.hours, a.minutes, a.seconds));
  };

  if (showResults && results) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrophyIcon color="success" />
            {isRelayEvent ? 'Relay Results Saved' : 'Race Results Saved'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" color="primary" gutterBottom>
              {results.created + results.updated}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isRelayEvent ? 'Total Relays Created/Updated' : 'Total PRs Created/Updated'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="success.main">
                {results.created}
              </Typography>
              <Typography variant="caption">{isRelayEvent ? 'New relays' : 'New PRs'}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="primary.main">
                {results.updated}
              </Typography>
              <Typography variant="caption">{isRelayEvent ? 'Relays updated' : 'PRs Updated'}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="text.secondary">
                {results.skipped}
              </Typography>
              <Typography variant="caption">Skipped</Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Details:
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {results.details.map((detail, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {detail.userName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {detail.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const selectedNoTime = selectedAthletes.filter(
    a => !a.dnf && !(a.hours || a.minutes || a.seconds)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {reviewMode ? <ReviewIcon color="primary" /> : <TrophyIcon />}
          {reviewMode ? 'Review race results' : 'Enter Race Results'}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {reviewMode ? (
          isRelayEvent ? (
            <>
              <Alert severity="warning" icon={<ReviewIcon />} sx={{ mb: 2 }}>
                Review relay results before saving.
              </Alert>
              {showRelaySumWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Legs sum differs from overall by {relaySumDeltaSec.toFixed(2)}s (tolerance 0.01s).
                </Alert>
              )}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Relay
                </Typography>
                <Typography variant="body2">
                  <strong>Event:</strong>{' '}
                  {EVENT_OPTIONS.find(o => o.value === eventType)?.label || eventType}
                  {' · '}
                  <strong>Date:</strong> {raceDate ? format(raceDate, 'MMM d, yyyy') : '—'}
                  {location.trim() ? (
                    <>
                      {' · '}
                      <strong>Location:</strong> {location.trim()}
                    </>
                  ) : null}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                  <strong>Overall:</strong>{' '}
                  {relayOverallHours || relayOverallMinutes || relayOverallSeconds
                    ? formatMinutesAsRaceTime(relayOverallTotalMinutesComputed)
                    : '—'}
                </Typography>
              </Paper>

              <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Leg</TableCell>
                      <TableCell>Athlete</TableCell>
                      <TableCell align="right">Split</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[0, 1, 2, 3].map((i) => {
                      const athleteId = relayLegAthleteIds[i];
                      const athlete = athletes.find(a => a.user.id === athleteId);
                      const splitMinutes = clockPartsToMinutes(
                        relayLegHours[i],
                        relayLegMinutes[i],
                        relayLegSeconds[i]
                      );
                      return (
                        <TableRow key={i}>
                          <TableCell>Leg {i + 1}</TableCell>
                          <TableCell>
                            {athlete?.user.displayName || '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontFamily="monospace">
                              {formatMinutesAsRaceTime(splitMinutes)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
          <>
            <Alert severity="warning" icon={<ReviewIcon />} sx={{ mb: 2 }}>
              Check every athlete and time below. PRs and race-day activities are only saved for athletes
              with a finish time (not DNF). When it all looks right, click <strong>Confirm &amp; save</strong>.
            </Alert>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Race
              </Typography>
              <Typography variant="body2">
                <strong>Event:</strong> {EVENT_OPTIONS.find(o => o.value === eventType)?.label || eventType}
                {' · '}
                <strong>Date:</strong> {raceDate ? format(raceDate, 'MMM d, yyyy') : '—'}
                {location.trim() ? (
                  <>
                    {' · '}
                    <strong>Location:</strong> {location.trim()}
                  </>
                ) : null}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                Saving <strong>{athletesWithTimeToSave.length}</strong> PR(s){selectedNoTime.length > 0 ? (
                  <> · <strong>{selectedNoTime.length}</strong> selected athlete(s) have no time (will not be saved)</>
                ) : null}
              </Typography>
            </Paper>
            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Athlete</TableCell>
                    <TableCell>Time / DNF</TableCell>
                    <TableCell>Previous PR</TableCell>
                    <TableCell>After save</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedAthletes.map(athlete => {
                    const willSave = !athlete.dnf && !!(athlete.hours || athlete.minutes || athlete.seconds);
                    return (
                      <TableRow key={athlete.user.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {athlete.user.displayName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {athlete.dnf ? (
                            <Chip label="DNF" size="small" color="error" />
                          ) : (
                            <Typography variant="body2" fontFamily="monospace">
                              {formatEnteredRaceTime(athlete)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {athlete.existingPR ? (
                            <Typography variant="caption">{formatMinutesAsRaceTime(athlete.existingPR.time)}</Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">No PR</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {!willSave ? (
                            <Chip label="Not saved" size="small" variant="outlined" />
                          ) : athlete.isPRBeat ? (
                            <Chip icon={<TrophyIcon />} label="New / better PR" size="small" color="success" />
                          ) : athlete.existingPR ? (
                            <Chip label="PR not beaten" size="small" />
                          ) : (
                            <Chip label="New PR" size="small" color="primary" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
          )
        ) : (
          <>
        {/* Race Info Section */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Race Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Event Type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              {EVENT_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Race Date"
                value={raceDate}
                onChange={(date) => setRaceDate(date)}
                maxDate={new Date()}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
              />
            </LocalizationProvider>

            <TextField
              label="Location (Optional)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              placeholder="e.g., District Meet"
            />
          </Box>
        </Paper>

        {!isRelayEvent && (
          <>
            {/* Athlete Selection Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Select Athletes ({selectedAthletes.length} selected)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleSelectAll} variant="outlined">
                Select All
              </Button>
              <Button size="small" onClick={handleClearAll} variant="outlined">
                Clear All
              </Button>
            </Box>
          </Box>
          
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Athletes tagged with "{eventType}" are auto-selected. Adjust selection as needed.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {athletes.map((athlete, index) => (
              <FormControlLabel
                key={athlete.user.id}
                control={
                  <Checkbox
                    checked={athlete.selected}
                    onChange={() => handleToggleSelect(index)}
                    size="small"
                  />
                }
                label={athlete.user.displayName}
              />
            ))}
          </Box>
            </Paper>
          </>
        )}

        {isRelayEvent && (
          <>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Relay Lineup (4 athletes)
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[0, 1, 2, 3].map((i) => {
                  const usedElsewhere = relayLegAthleteIds
                    .map((id, idx) => (idx !== i ? id : ''))
                    .filter(Boolean);
                  return (
                    <TextField
                      key={i}
                      select
                      label={`Leg ${i + 1} Athlete`}
                      value={relayLegAthleteIds[i]}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRelayLegAthleteIds((prev) => {
                          const next = [...prev];
                          next[i] = v;
                          return next;
                        });
                      }}
                      size="small"
                      sx={{ minWidth: 240 }}
                    >
                      {athletes
                        .filter(
                          (a) => a.user.role !== 'coach' && a.user.role !== 'headCoach'
                        )
                        .map((a) => (
                          <MenuItem
                            key={a.user.id}
                            value={a.user.id}
                            disabled={
                              usedElsewhere.includes(a.user.id) &&
                              relayLegAthleteIds[i] !== a.user.id
                            }
                          >
                            {a.user.displayName}
                          </MenuItem>
                        ))}
                    </TextField>
                  );
                })}
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Tags (if available) are used as a suggestion; you can still select anyone.
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Relay Times (overall + 4 legs)
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Overall time
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  label="Hours"
                  type="number"
                  value={relayOverallHours}
                  onChange={(e) => setRelayOverallHours(e.target.value)}
                  inputProps={{ min: 0, max: 23 }}
                  size="small"
                  sx={{ width: 90 }}
                />
                <TextField
                  label="Minutes"
                  type="number"
                  value={relayOverallMinutes}
                  onChange={(e) => setRelayOverallMinutes(e.target.value)}
                  inputProps={{ min: 0, max: 59 }}
                  size="small"
                  sx={{ width: 110 }}
                />
                <TextField
                  label="Seconds"
                  type="number"
                  value={relayOverallSeconds}
                  onChange={(e) => setRelayOverallSeconds(e.target.value)}
                  inputProps={{ min: 0, max: 59.99, step: 0.01 }}
                  size="small"
                  sx={{ width: 130 }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Leg splits
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[0, 1, 2, 3].map((i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ width: 54 }}>
                      Leg {i + 1}
                    </Typography>
                    <TextField
                      label="H"
                      type="number"
                      value={relayLegHours[i]}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRelayLegHours((prev) => {
                          const next = [...prev];
                          next[i] = v;
                          return next;
                        });
                      }}
                      inputProps={{ min: 0, max: 23 }}
                      size="small"
                      sx={{ width: 90 }}
                    />
                    <TextField
                      label="M"
                      type="number"
                      value={relayLegMinutes[i]}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRelayLegMinutes((prev) => {
                          const next = [...prev];
                          next[i] = v;
                          return next;
                        });
                      }}
                      inputProps={{ min: 0, max: 59 }}
                      size="small"
                      sx={{ width: 110 }}
                    />
                    <TextField
                      label="S"
                      type="number"
                      value={relayLegSeconds[i]}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRelayLegSeconds((prev) => {
                          const next = [...prev];
                          next[i] = v;
                          return next;
                        });
                      }}
                      inputProps={{ min: 0, max: 59.99, step: 0.01 }}
                      size="small"
                      sx={{ width: 130 }}
                    />
                  </Box>
                ))}
              </Box>

              {showRelaySumWarning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Legs sum to {relayLegTotalMinutes.reduce((a, b) => a + b, 0).toFixed(3)} min, but overall is{' '}
                  {relayOverallTotalMinutesComputed.toFixed(3)} min (difference {relaySumDeltaSec.toFixed(2)}s).
                </Alert>
              )}
            </Paper>
          </>
        )}

        {!isRelayEvent && (
          <>
            {/* Results Entry Table */}
            {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : selectedAthletes.length === 0 ? (
          <Alert severity="info">
            Select at least one athlete to enter race results
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Athlete</TableCell>
                  <TableCell>Existing PR</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Minutes</TableCell>
                  <TableCell>Seconds (0.00)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>DNF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAthletes.map((athlete, selectedIndex) => {
                  const actualIndex = athletes.findIndex(a => a.user.id === athlete.user.id);
                  
                  return (
                    <TableRow key={athlete.user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {athlete.user.displayName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {athlete.existingPR ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatMinutesAsRaceTime(athlete.existingPR.time)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No PR
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={athlete.hours}
                          onChange={(e) => handleTimeChange(actualIndex, 'hours', e.target.value)}
                          placeholder="0"
                          size="small"
                          disabled={athlete.dnf}
                          inputProps={{ min: 0, style: { width: '60px' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={athlete.minutes}
                          onChange={(e) => handleTimeChange(actualIndex, 'minutes', e.target.value)}
                          placeholder="0"
                          size="small"
                          disabled={athlete.dnf}
                          inputProps={{ min: 0, max: 59, style: { width: '60px' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={athlete.seconds}
                          onChange={(e) => handleTimeChange(actualIndex, 'seconds', e.target.value)}
                          placeholder="0.00"
                          size="small"
                          disabled={athlete.dnf}
                          inputProps={{
                            min: 0,
                            max: 59.99,
                            step: 0.01,
                            style: { width: '76px' },
                            title: 'Seconds, up to 2 decimal places (e.g. chip time)',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {athlete.dnf ? (
                          <Chip label="DNF" size="small" color="error" />
                        ) : athlete.isPRBeat ? (
                          <Chip icon={<TrophyIcon />} label="PR!" size="small" color="success" />
                        ) : athlete.existingPR && (athlete.hours || athlete.minutes || athlete.seconds) ? (
                          <Chip label="Not PR" size="small" />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={athlete.dnf}
                          onChange={(e) => handleDNFChange(actualIndex, e.target.checked)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          </>
        )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {reviewMode ? (
          <>
            <Button startIcon={<BackIcon />} onClick={() => setReviewMode(false)} disabled={saving}>
              Back to edit
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSaveResults}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            >
              {saving ? 'Saving…' : 'Confirm & save'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={goToReview}
              disabled={
                saving ||
                loading ||
                (isRelayEvent
                  ? !relayLegsUnique || !relayLegsFilled || !relayOverallFilled
                  : selectedAthletes.length === 0)
              }
              startIcon={<ReviewIcon />}
            >
              Review &amp; confirm
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkRaceResultsEntry;

